// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import {
    addToChainAfter,
    addToEndOfChain,
    makeHandlerChainAwaitable,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import {
    chain,
    PopulateApiParameter,
    SetCreditCard,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/PlaceOrder";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";

const XngPopulateApiParameter: typeof PopulateApiParameter = props => {
    const state = props.getState();
    const { orderNotes, shipPartial, releaseNumber, deliveryAccount } = (state as CustomAppState).xCheckout;
    const carrierId = props.apiParameter.cart.carrier?.id;
    const settingCollectId = getSettingsCollection(state).xngShippingSettings?.collectCarrierId;
    props.apiParameter = {
        cart: {
            ...props.apiParameter.cart,
            notes: orderNotes || "",
            properties: {
                ...props.apiParameter.cart.properties,
                pscShipPartial: `${shipPartial}`,
                shippingAccountCarrier: settingCollectId === carrierId ? deliveryAccount.carrier : "", // reset them
                shippingAccountNumber: settingCollectId === carrierId ? deliveryAccount.number : "", // reset them
                releaseNumber,
            },
        },
    };
};

export const XngSetCreditCard: typeof SetCreditCard = props => {
    const { cartToUpdate } = props;

    if (cartToUpdate.paymentMethod?.isCreditCard && !props.parameter.isAdyenDropIn) {
        const updatedCreditCard = {
            ...cartToUpdate.paymentOptions!.creditCard!,
            cardHolderName: props.parameter.cardHolderName,
            cardNumber: props.parameter.cardNumber,
            cardType: props.parameter.cardType,
            expirationMonth: props.parameter.expirationMonth,
            expirationYear: props.parameter.expirationYear,
            securityCode: props.parameter.securityCode,
            useBillingAddress: props.parameter.useBillingAddress,
            maskedCardNumber: props.parameter.maskedCardNumber,
        };

        cartToUpdate.paymentOptions!.storePaymentProfile = props.parameter.saveCard;

        if (!props.parameter.useBillingAddress) {
            const countries = getCurrentCountries(props.getState());
            if (!countries) {
                throw new Error("The active countries for the current website are not available.");
            }

            const selectedCountry = countries.find(country => country.id === props.parameter.countryId);
            if (!selectedCountry) {
                throw new Error("The selected country for the card billing address is not valid.");
            }

            updatedCreditCard.address1 = props.parameter.address1;
            // <XNG-Change>
            (updatedCreditCard as any).address2 = (props.parameter as any).address2;
            // </XNG-Change>
            updatedCreditCard.country = selectedCountry.name;
            updatedCreditCard.countryAbbreviation = selectedCountry.abbreviation;

            if (!!selectedCountry.states && selectedCountry.states.length > 0) {
                const selectedState = selectedCountry.states?.find(state => state.id === props.parameter.stateId);
                if (!selectedState) {
                    throw new Error("The selected state for the card billing address is not valid.");
                }

                updatedCreditCard.state = selectedState.name;
                updatedCreditCard.stateAbbreviation = selectedState.abbreviation;
            }

            updatedCreditCard.city = props.parameter.city;
            updatedCreditCard.postalCode = props.parameter.postalCode;
        }

        cartToUpdate.paymentOptions!.creditCard = updatedCreditCard;
    }

    if (cartToUpdate.paymentMethod?.isCreditCard || cartToUpdate.paymentMethod?.isPaymentProfile) {
        const spreedlyFrame = getSpreedlyFrame();
        if (spreedlyFrame?.isSpreedly()) {
            cartToUpdate.paymentOptions!.creditCard!.browserInfo = spreedlyFrame.getBrowserInfo();
        }
    }
};

export const DispatchDataCleanup: any = (props: any) => {
    props.dispatch({
        type: "xCheckout/ResetAll",
    } as any);
};

addToChainAfter(chain, PopulateApiParameter, XngPopulateApiParameter);
replaceInChain(chain, SetCreditCard, XngSetCreditCard);
addToEndOfChain(chain, DispatchDataCleanup);
